// ProjectMeasurements.js - Same table style as MeasurementData but simplified filtering

'use client';

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../supabaseClient';
import MeasurementsMap from '../../components/MeasurementMap';
import OnStationModal from '../Integrations/OnStationModal';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
// Icons
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  EllipsisVerticalIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  MapIcon,
  PaintBrushIcon,
  InformationCircleIcon,
  ChevronUpIcon,
  ChevronDownIcon as ChevronDownIconSolid,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { EyeIcon as EyeIconSolid, MapPinIcon } from '@heroicons/react/20/solid';


// Performance constants
const MAX_RECORDS = 50000;
const MAX_MAP_MARKERS = 10000;
const ROW_HEIGHT = 22;
const VISIBLE_ROWS = 10;

// Default quality thresholds
const DEFAULT_THRESHOLDS = {
  compaction: { pass: 92.0, warning: 88.0, fail: 0.0 },
  density: { pass: 145.0, warning: 140.0, fail: 0.0 }
};

// Quality colors
const PASS_COLOR = { bg: '#dcfce7', text: '#16a34a' };
const FAIL_COLOR = { bg: '#fef2f2', text: '#dc2626' };



// Column definitions - same as MeasurementData but project-focused
const columns = [
  { 
    key: 'LocalTimeOfMeasurement', 
    label: 'Local Time', 
    sortable: true,
    width: 160,
    minWidth: 140,
    type: 'datetime',
    format: (value) => value ? new Date(value).toLocaleString() : ''
  },
  { 
    key: 'operator_name', 
    label: 'Uploaded by', 
    sortable: false,
    width: 140,
    minWidth: 100,
    type: 'text'
  },
  { 
    key: 'Density', 
    label: 'Density', 
    sortable: true,
    width: 90,
    minWidth: 70,
    type: 'number',
    format: (value) => value?.toFixed(1) || ''
  },
  { 
    key: 'SurfaceTemp', 
    label: 'Surface Temp', 
    sortable: true,
    width: 110,
    minWidth: 90,
    type: 'number',
    format: (value) => value?.toFixed(1) || ''
  },
  { 
    key: 'Compaction', 
    label: 'Compaction', 
    sortable: true,
    width: 100,
    minWidth: 80,
    type: 'number',
    format: (value) => value?.toFixed(1) || ''
  },
  { 
    key: 'Voids', 
    label: 'Voids', 
    sortable: true,
    width: 80,
    minWidth: 60,
    type: 'number',
    format: (value) => value?.toFixed(1) || ''
  },
  { 
    key: 'Description1', 
    label: 'Description', 
    sortable: false,
    width: 200,
    minWidth: 150,
    type: 'text'
  },
  { 
    key: 'Station', 
    label: 'Station', 
    sortable: true,
    width: 100,
    minWidth: 70,
    type: 'text'
  },
  { 
    key: 'Operator', 
    label: 'Operator', 
    sortable: true,
    width: 140,
    minWidth: 100,
    type: 'text'
  }
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

// Map Toggle Component
const MapToggle = ({ isMapVisible, onChange }) => {
  return (
    <label className={`group relative inline-flex w-12 h-6 shrink-0 rounded-full p-0.5 ring-1 ring-gray-900/5 transition-colors duration-200 ease-in-out cursor-pointer ${isMapVisible ? 'bg-green-600' : 'bg-gray-200'}`}>
      <span className={`relative w-5 h-5 rounded-full bg-white shadow-sm ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out ${isMapVisible ? 'translate-x-6' : 'translate-x-0'}`}>
        <span
          aria-hidden="true"
          className={`absolute inset-0 flex w-full h-full items-center justify-center transition-opacity duration-100 ease-out ${isMapVisible ? 'opacity-0 duration-100 ease-out' : 'opacity-100'}`}
        >
          <MapIcon className="w-3 h-3 text-gray-400" />
        </span>
        <span
          aria-hidden="true"
          className={`absolute inset-0 flex w-full h-full items-center justify-center transition-opacity duration-100 ease-out ${isMapVisible ? 'opacity-100 duration-200 ease-in' : 'opacity-0'}`}
        >
          <MapIcon className="w-3 h-3 text-green-600" />
        </span>
      </span>
      <input
        type="checkbox"
        checked={isMapVisible}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
    </label>
  );
};

// Tooltip Component
const Tooltip = ({ children, content }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const showTooltip = (e) => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX + rect.width / 2
      });
    }
    setIsVisible(true);
  };

  const hideTooltip = () => {
    setIsVisible(false);
  };

  const tooltip = isVisible ? (
    <div
      className="fixed z-[10000] px-2 py-1 text-xs text-white bg-gray-800 rounded shadow-lg pointer-events-none transform -translate-x-1/2"
      style={{
        top: position.top,
        left: position.left,
        maxWidth: '200px'
      }}
    >
      {content}
      <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45"></div>
    </div>
  ) : null;

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        className="cursor-help"
      >
        {children}
      </span>
      {tooltip && createPortal(tooltip, document.body)}
    </>
  );
};

// Custom Date Filter Dropdown
const DateFilterDropdown = ({ isOpen, onClose, buttonRef, startDate, endDate, onStartDateChange, onEndDateChange }) => {
  const dropdownRef = useRef(null);

  const dropdownPosition = useMemo(() => {
    if (!isOpen || !buttonRef.current) {
      return { top: 0, left: 0, width: 400 };
    }
    
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = 350; // Approximate height

    return {
      top: spaceBelow > dropdownHeight ? rect.bottom + 4 : rect.top - dropdownHeight - 4,
      left: rect.left,
      width: Math.max(rect.width, 400)
    };
  }, [isOpen, buttonRef]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen &&
          dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, buttonRef]);

  const presets = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Last 7 days', value: '7days' },
    { label: 'Last 30 days', value: '30days' },
    { label: 'This month', value: 'thisMonth' },
    { label: 'Last month', value: 'lastMonth' },
    { label: 'This year', value: 'thisYear' },
    { label: 'All time', value: 'allTime' }
  ];

  const applyPreset = (preset) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day
    const formatDate = (date) => date.toISOString().split('T')[0];

    switch (preset) {
      case 'today':
        onStartDateChange(formatDate(today));
        onEndDateChange(formatDate(today));
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        onStartDateChange(formatDate(yesterday));
        onEndDateChange(formatDate(yesterday));
        break;
      case '7days':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 6);
        onStartDateChange(formatDate(weekAgo));
        onEndDateChange(formatDate(new Date()));
        break;
      case '30days':
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 29);
        onStartDateChange(formatDate(monthAgo));
        onEndDateChange(formatDate(new Date()));
        break;
      case 'thisMonth':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        onStartDateChange(formatDate(monthStart));
        onEndDateChange(formatDate(new Date()));
        break;
      case 'lastMonth':
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        onStartDateChange(formatDate(lastMonthStart));
        onEndDateChange(formatDate(lastMonthEnd));
        break;
      case 'thisYear':
        const yearStart = new Date(today.getFullYear(), 0, 1);
        onStartDateChange(formatDate(yearStart));
        onEndDateChange(formatDate(new Date()));
        break;
      case 'allTime':
        onStartDateChange('');
        onEndDateChange('');
        break;
    }
  };

  const clearDates = () => {
    onStartDateChange('');
    onEndDateChange('');
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl ring-1 ring-black ring-opacity-5"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Date Filter</h3>
          <button
            onClick={onClose}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500"
          >
            Close
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex">
        {/* Presets */}
        <div className="w-40 border-r border-gray-200 bg-gray-50">
          <div className="p-2">
            <div className="space-y-1">
              {presets.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => applyPreset(preset.value)}
                  className="w-full text-left px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-100 rounded transition-colors"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Custom Date Range */}
        <div className="flex-1 p-4">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => onStartDateChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => onEndDateChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            {(startDate || endDate) && (
              <button
                onClick={clearDates}
                className="w-full px-3 py-2 text-xs text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Clear Dates
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
      </div>
    </div>,
    document.body
  );
};

// Simplified Conditional Formatting Dropdown
const ConditionalFormattingDropdown = ({ isOpen, onClose, onAddRule, buttonRef, activeRules, onRemoveRule }) => {
  const [selectedColumn, setSelectedColumn] = useState('');
  const [threshold, setThreshold] = useState('');
  const dropdownRef = useRef(null);

  const dropdownPosition = useMemo(() => {
    if (!isOpen || !buttonRef.current) {
      return { top: 0, left: 0, width: 400 };
    }
    
    const rect = buttonRef.current.getBoundingClientRect();
    return {
      top: rect.bottom,
      left: rect.left,
      width: Math.max(rect.width, 400)
    };
  }, [isOpen, buttonRef]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen &&
          dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, buttonRef]);

  const numberColumnOptions = columns
    .filter(col => col.type === 'number')
    .map(col => ({ value: col.key, label: col.label }));

  const handleAddRule = () => {
    if (selectedColumn && threshold !== '') {
      const newRule = {
        id: Date.now() + Math.random(),
        column: selectedColumn,
        threshold: parseFloat(threshold),
        type: 'threshold',
        enabled: true
      };
      
      onAddRule(newRule);
      setSelectedColumn('');
      setThreshold('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl ring-1 ring-black ring-opacity-5"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Add Formatting Rule</h3>
          <button
            onClick={() => {
              setSelectedColumn('');
              setThreshold('');
              onClose();
            }}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-200 transition-colors"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Active Rules */}
      {activeRules.length > 0 && (
        <div className="px-4 py-2 border-b border-gray-200 bg-gray-25">
          <div className="text-xs font-medium text-gray-700 mb-2">Active Rules:</div>
          <div className="space-y-1">
            {activeRules.map((rule) => (
              <div key={rule.id} className="flex items-center justify-between text-xs bg-white rounded px-2 py-1 border">
                <div className="flex items-center space-x-2">
                  <span className="font-medium">{columns.find(col => col.key === rule.column)?.label}</span>
                  <span className="text-gray-500">≥ {rule.threshold}</span>
                  <div className="flex items-center space-x-1">
                    <div 
                      className="px-1 py-0.5 rounded text-xs"
                      style={{ backgroundColor: PASS_COLOR.bg, color: PASS_COLOR.text }}
                    >
                      Pass
                    </div>
                    <div 
                      className="px-1 py-0.5 rounded text-xs"
                      style={{ backgroundColor: FAIL_COLOR.bg, color: FAIL_COLOR.text }}
                    >
                      Fail
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => onRemoveRule(rule.id)}
                  className="text-red-400 hover:text-red-600"
                  title="Remove rule"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form */}
      <div className="p-4">
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Column</label>
            <select
              value={selectedColumn}
              onChange={(e) => setSelectedColumn(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="">Select column</option>
              {numberColumnOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Threshold (Pass if ≥)</label>
            <input
              type="number"
              step="0.1"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="e.g., 92"
              disabled={!selectedColumn}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-2 pt-3 border-t border-gray-200">
          <button
            onClick={() => {
              setSelectedColumn('');
              setThreshold('');
              onClose();
            }}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            Cancel
          </button>
          <button
            onClick={handleAddRule}
            disabled={!selectedColumn || threshold === ''}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Rule
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default function ProjectMeasurements({
  projectId,
  projectData,
  memberIds,
  organizationId,
  projectPolygon,
  availableIntegrations,
  user,
  onExportCSV
}) {
  // Core data states
  const [measurements, setMeasurements] = useState([]);
  const [mapMeasurements, setMapMeasurements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);

  // Table state
  const [sortBy, setSortBy] = useState('LocalTimeOfMeasurement');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [selectedRows, setSelectedRows] = useState([]);
  const [allSelected, setAllSelected] = useState(false);

  // Simple filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOperator, setSelectedOperator] = useState('');
  const [operators, setOperators] = useState([]); // This will be populated with all operators
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Conditional formatting state
  const [conditionalFormattingRules, setConditionalFormattingRules] = useState([]);
  const [showFormattingDropdown, setShowFormattingDropdown] = useState(false);
  const [showDateFilter, setShowDateFilter] = useState(false);

  // UI states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDetailsMeasurement, setSelectedDetailsMeasurement] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Column resizing state
  const [columnWidths, setColumnWidths] = useState(() => {
    const widths = {};
    columns.forEach(col => {
      widths[col.key] = col.width;
    });
    return widths;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  // Map states
  const [selectedMapMeasurement, setSelectedMapMeasurement] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 42.73116, lng: -73.68657 });
  const [isMapVisible, setIsMapVisible] = useState(true);
  const [mapStyle, setMapStyle] = useState([]);

  // Modal states
  const [showOnStationModal, setShowOnStationModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [measurementToDelete, setMeasurementToDelete] = useState(null);
  const [deletingMeasurement, setDeletingMeasurement] = useState(false);

  // Integration states
  const [sentIntegrations, setSentIntegrations] = useState({});

  // Refs
  const debounceTimeoutRef = useRef(null);
  const exportMenuRef = useRef(null);
  const formattingButtonRef = useRef(null);
  const dateFilterButtonRef = useRef(null);
  const tableRef = useRef(null);

  const integrations = ['OnStation', 'HaulHub'];
  
  // New `useMemo` to derive the list of operators from the fetched measurements
  const operatorsMemo = useMemo(() => {
    if (!measurements || measurements.length === 0) return [];
    
    const uniqueOperators = new Set();
    measurements.forEach(m => {
      if (m.operator_name && m.operator_name.trim() !== 'Unknown') {
        uniqueOperators.add(m.operator_name);
      }
    });

    return Array.from(uniqueOperators).sort();
  }, [measurements]);

  // Conditional formatting logic
  const getCellStyle = useCallback((value, columnKey, measurement) => {
    const applicableRule = conditionalFormattingRules
      .find(rule => rule.enabled && rule.column === columnKey);

    if (!applicableRule || value === null || value === undefined) {
      return {};
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return {};

    const color = numValue >= applicableRule.threshold ? PASS_COLOR : FAIL_COLOR;
    return {
      backgroundColor: color.bg,
      color: color.text,
      fontWeight: '500'
    };
  }, [conditionalFormattingRules]);

  // Column resizing handlers (same as MeasurementData)
  const handleMouseDown = useCallback((e, columnKey) => {
    e.preventDefault();
    setIsResizing(true);
    setResizingColumn(columnKey);
    setStartX(e.clientX);
    setStartWidth(columnWidths[columnKey]);
  }, [columnWidths]);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing || !resizingColumn) return;
    
    const diff = e.clientX - startX;
    const newWidth = Math.max(
      startWidth + diff,
      columns.find(col => col.key === resizingColumn)?.minWidth || 60
    );
    
    setColumnWidths(prev => ({
      ...prev,
      [resizingColumn]: newWidth
    }));
  }, [isResizing, resizingColumn, startX, startWidth]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    setResizingColumn(null);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // *** The core fix is here: a new, separate function to get all operators ***
  const fetchAllOperators = useCallback(async () => {
    if (!projectId || memberIds.length === 0) {
      setOperators([]);
      return;
    }

    setLoading(true);
    try {
      // Query to get only the unique operator names for the project
      // We use a specific query to avoid fetching all measurement data
      const { data, error } = await supabase
        .from('measurements_with_details')
        .select('operator_name')
        .in('profile_id', memberIds)
        .eq('project_id', projectId)
        .not('operator_name', 'is', null) // Filter out null operators
        .order('operator_name', { ascending: true }); // Sort for dropdown display

      if (error) throw error;
      
      const uniqueOperators = [...new Set(data.map(m => m.operator_name))];
      setOperators(uniqueOperators.filter(op => op.trim() !== 'Unknown')); // Filter and set
    } catch (err) {
      console.error('Error fetching all operators:', err);
      setOperators([]);
    } finally {
      // It's important to not set `loading` to false here, as `fetchMeasurements` is the main loading state
    }
  }, [projectId, memberIds]);

  // Effect to trigger the new operator fetch function
  useEffect(() => {
    fetchAllOperators();
  }, [fetchAllOperators]);
  
  // Fetch measurements with simplified filtering
  const fetchMeasurements = useCallback(async (page = 1) => {
    if (!user || !projectId || memberIds.length === 0) return;

    setLoading(true);
    try {
      const from = (page - 1) * rowsPerPage;
      const to = from + rowsPerPage - 1;

      let query = supabase
        .from('measurements_with_details')
        .select('*', { count: 'exact' })
        .in('profile_id', memberIds)
        .eq('project_id', projectId)
        .limit(MAX_RECORDS);

      // Simple filters
      if (selectedOperator) {
        query = query.eq('operator_name', selectedOperator);
      }
      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        query = query.gte('LocalTimeOfMeasurement', start.toISOString());
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        query = query.lte('LocalTimeOfMeasurement', end.toISOString());
      }
      if (searchTerm) {
        query = query.or(`operator_name.ilike.%${searchTerm}%,Operator.ilike.%${searchTerm}%,Description1.ilike.%${searchTerm}%,Description2.ilike.%${searchTerm}%,Station.ilike.%${searchTerm}%`);
      }

      const { data: tableData, count, error: tableError } = await query
        .range(from, to)
        .order(sortBy, { ascending: sortOrder === 'asc' });

      if (tableError) throw tableError;

      setMeasurements(tableData || []);
      setTotalRecords(count || 0);

      // Fetch map data separately
      let mapQuery = supabase
        .from('measurements_with_details')
        .select('id, latitude, longitude, Density, Compaction, SurfaceTemp, operator_name, project_display_name, LocalTimeOfMeasurement, Description1')
        .in('profile_id', memberIds)
        .eq('project_id', projectId)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null)
        .limit(MAX_MAP_MARKERS);

      // Apply same filters to map data
      if (selectedOperator) {
        mapQuery = mapQuery.eq('operator_name', selectedOperator);
      }
      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        mapQuery = mapQuery.gte('LocalTimeOfMeasurement', start.toISOString());
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        mapQuery = mapQuery.lte('LocalTimeOfMeasurement', end.toISOString());
      }
      if (searchTerm) {
        mapQuery = mapQuery.or(`operator_name.ilike.%${searchTerm}%,Operator.ilike.%${searchTerm}%,Description1.ilike.%${searchTerm}%,Description2.ilike.%${searchTerm}%,Station.ilike.%${searchTerm}%`);
      }

      const { data: mapData, error: mapError } = await mapQuery.order(sortBy, { ascending: sortOrder === 'asc' });

      if (mapError) throw mapError;

      setMapMeasurements(mapData || []);

      if (mapData && mapData.length > 0 && mapData[0].latitude && mapData[0].longitude) {
        setMapCenter({ lat: mapData[0].latitude, lng: mapData[0].longitude });
      }

    } catch (err) {
      console.error('Error fetching measurements:', err);
      setMeasurements([]);
      setMapMeasurements([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [user, projectId, memberIds, selectedOperator, startDate, endDate, searchTerm, sortBy, sortOrder, rowsPerPage]);

  // Effect for debounced filter changes
  useEffect(() => {
    if (!user || !projectId || memberIds.length === 0) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchMeasurements(1);
    }, 500);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchTerm, selectedOperator, startDate, endDate, sortBy, sortOrder, rowsPerPage, fetchMeasurements, projectId, memberIds]);


  // Fetch on page change
  useEffect(() => {
    if (!user || memberIds.length === 0) return;
    
    fetchMeasurements(currentPage);
  }, [currentPage, fetchMeasurements, user, memberIds]);

  // Fetch sent integrations
  useEffect(() => {
    const fetchSentIntegrations = async () => {
      const ids = measurements.map(m => m.id);
      if (!ids.length) return setSentIntegrations({});
  
      const { data, error } = await supabase
        .from('integrations_log')
        .select('measurement_id, integration')
        .in('integration', integrations)
        .in('measurement_id', ids);
  
      if (!error) {
        const map = {};
        data.forEach(({ measurement_id, integration }) => {
          map[measurement_id] ??= new Set();
          map[measurement_id].add(integration);
        });
        setSentIntegrations(map);
      }
    };
    fetchSentIntegrations();
  }, [measurements, currentPage, showOnStationModal]);

  // Handle sorting
  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnKey);
      setSortOrder('desc');
    }
  };

  // Handle row selection
  const toggleRowSelection = (measurement) => {
    setSelectedRows(prev => {
      if (prev.find(row => row.id === measurement.id)) {
        return prev.filter(row => row.id !== measurement.id);
      } else {
        return [...prev, measurement];
      }
    });
  };

  const toggleAllSelection = () => {
    if (allSelected) {
      setSelectedRows([]);
      setAllSelected(false);
    } else {
      setSelectedRows([...measurements]);
      setAllSelected(true);
    }
  };

  // Conditional formatting management
  const handleAddFormattingRule = (rule) => {
    setConditionalFormattingRules(prev => [
      ...prev.filter(r => r.column !== rule.column),
      rule
    ]);
  };

  const handleRemoveFormattingRule = (ruleId) => {
    setConditionalFormattingRules(prev => prev.filter(r => r.id !== ruleId));
  };

  // Action handlers
  const handleViewDetails = (measurement) => {
    setSelectedDetailsMeasurement(measurement);
    setShowDetailsModal(true);
    setActiveDropdown(null);
  };

  const handleViewOnMap = (measurement) => {
    if (!isMapVisible) {
      setIsMapVisible(true);
    }
    
    setSelectedMapMeasurement(measurement.id);
    
    if (measurement.latitude && measurement.longitude) {
      setMapCenter({ 
        lat: parseFloat(measurement.latitude), 
        lng: parseFloat(measurement.longitude) 
      });
    }
    
    setTimeout(() => {
      const mapSection = document.querySelector('.h-\\[40vh\\]');
      if (mapSection) {
        mapSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
    
    setActiveDropdown(null);
  };

  // Export functionality
  const exportToCSV = (data, filename = "measurements.csv") => {
    if (!data || data.length === 0) return;

    const headers = columns.map(col => col.label);
    const rows = data.map(row =>
      columns.map(col => {
        let value = row[col.key];
        if (col.format && value !== null && value !== undefined) {
          value = col.format(value, row);
        }
        if (typeof value === "string") {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value !== null && value !== undefined ? value : "";
      }).join(",")
    );

    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  // Clear map selection when filters change
  useEffect(() => {
    setSelectedMapMeasurement(null);
  }, [searchTerm, selectedOperator, startDate, endDate]);

  const dateFilterDisplay = useMemo(() => {
    if (!startDate && !endDate) return 'All Time';
    if (startDate && !endDate) return `After ${new Date(startDate).toLocaleDateString()}`;
    if (!startDate && endDate) return `Before ${new Date(endDate).toLocaleDateString()}`;
    if (startDate === endDate) return new Date(startDate).toLocaleDateString();
    return `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`;
  }, [startDate, endDate]);

  return (
    <div className="h-full flex flex-col">
      {/* Map Section - Fixed height when visible */}
      <div className={`flex-shrink-0 transition-all duration-300 ease-in-out ${isMapVisible ? 'h-[40vh]' : 'h-0'} overflow-hidden w-full`}>
        <div className="h-full rounded-lg border border-gray-200 overflow-hidden">
          <MeasurementsMap
            measurements={mapMeasurements}
            selectedMeasurement={selectedMapMeasurement}
            geofencePolygon={projectPolygon}
            mapCenter={mapCenter}
            mapZoom={13}
            mapStyle={mapStyle}
            onMarkerClick={(measurement) => setSelectedMapMeasurement(measurement.id)}
            onStyleChange={setMapStyle}
            height="100%"
            showStyleControls={true}
            showMeasurementCount={true}
          />
        </div>
      </div>

      {/* Controls Section - Fixed height */}
      <div className="flex-shrink-0 py-3">
        {/* Main controls row */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search..."
              className="block w-64 pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Custom Operator Dropdown */}
          <Menu as="div" className="relative inline-block text-left">
              <MenuButton className="inline-flex items-center justify-between gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 min-w-[150px]">
                <span className="truncate">{selectedOperator || 'All Operators'}</span>
                <ChevronDownIconSolid aria-hidden="true" className="-mr-1 h-4 w-4 text-gray-400" />
              </MenuButton>
              <MenuItems
                transition
                className="absolute left-0 z-50 mt-2 w-56 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[enter]:ease-out data-[leave]:duration-75 data-[leave]:ease-in"
              >
                <div className="py-1 max-h-60 overflow-y-auto">
                    <MenuItem>
                        <button onClick={() => setSelectedOperator('')} className={classNames('block w-full text-left px-4 py-2 text-sm', selectedOperator === '' ? 'bg-gray-100 text-gray-900' : 'text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900')}>
                            All Operators
                        </button>
                    </MenuItem>
                    {operators.map((op) => (
                    <MenuItem key={op}>
                        <button onClick={() => setSelectedOperator(op)} className={classNames('block w-full text-left px-4 py-2 text-sm', selectedOperator === op ? 'bg-gray-100 text-gray-900' : 'text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900')}>
                            {op}
                        </button>
                    </MenuItem>
                    ))}
                </div>
            </MenuItems>
          </Menu>

           {/* Custom Date Filter Button */}
          <button
            ref={dateFilterButtonRef}
            onClick={() => setShowDateFilter(!showDateFilter)}
            className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            <CalendarDaysIcon className="h-4 w-4 text-gray-500" />
            <span className="truncate">{dateFilterDisplay}</span>
            <ChevronDownIconSolid className="-mr-1 h-4 w-4 text-gray-400" />
          </button>

          {/* Conditional Formatting Button */}
          <button
            ref={formattingButtonRef}
            onClick={() => setShowFormattingDropdown(!showFormattingDropdown)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <PaintBrushIcon className="h-4 w-4 mr-1" />
            Formatting
            {conditionalFormattingRules.filter(r => r.enabled).length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                {conditionalFormattingRules.filter(r => r.enabled).length}
              </span>
            )}
          </button>

          {/* Spacer to push right-side controls */}
          <div className="flex-1" />

          {/* Map Toggle */}
          <MapToggle 
            isMapVisible={isMapVisible}
            onChange={setIsMapVisible}
          />

          {/* Export */}
          <div className="relative" ref={exportMenuRef}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={selectedRows.length === 0}
              className={`inline-flex items-center px-3 py-2 border shadow-sm text-sm leading-4 font-medium rounded-md ${
                selectedRows.length > 0
                  ? 'border-green-300 text-green-700 bg-green-50 hover:bg-green-100'
                  : 'border-gray-300 text-gray-400 bg-gray-50 cursor-not-allowed'
              }`}
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
              Export ({selectedRows.length})
            </button>
            {showExportMenu && selectedRows.length > 0 && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                {availableIntegrations?.OnStation && (
      <button
        onClick={() => {
          setShowOnStationModal(true);
          setShowExportMenu(false);
        }}
        className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
      >
        Send to OnStation
      </button>
    )}
                <button
                  onClick={() => {
                    exportToCSV(selectedRows, "selected_measurements.csv");
                    setShowExportMenu(false);
                    setSelectedRows([]);
                    setAllSelected(false);
                  }}
                  className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                >
                  Export Selected
                </button>
                <button
                  onClick={() => {
                    exportToCSV(measurements, "all_measurements.csv");
                    setShowExportMenu(false);
                  }}
                  className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                >
                  Export All
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Performance warning */}
        {totalRecords > 10000 && (
          <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
            <p className="text-xs text-amber-700">
              <strong>Large dataset:</strong> {totalRecords.toLocaleString()} records. Consider using filters for better performance.
            </p>
          </div>
        )}
      </div>

      {/* Table Section - Takes remaining height */}
      <div className="flex-1 min-h-0 flex flex-col w-full">
        <div className="h-full bg-white shadow border border-gray-200 rounded-lg flex flex-col w-full overflow-hidden">
          {/* Table content - scrollable */}
          <div className="flex-1 overflow-auto">
            <table className="min-w-full table-fixed border-collapse">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-20 shadow-sm overflow-hidden">
                <tr>
                  <th className="w-12 px-3 py-2 border-r border-gray-200 bg-gray-50 overflow-hidden">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      checked={allSelected && measurements.length > 0 && selectedRows.length === measurements.length}
                      onChange={toggleAllSelection}
                    />
                  </th>
                  <th className="w-24 px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-r border-gray-200">
                    Actions
                  </th>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className="relative px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 bg-gray-50"
                      style={{ width: `${columnWidths[column.key]}px` }}
                    >
                      <div 
                        className={`flex items-center space-x-1 ${
                          column.sortable ? 'cursor-pointer hover:bg-gray-100 rounded px-1 py-0.5' : ''
                        }`}
                        onClick={() => column.sortable && handleSort(column.key)}
                      >
                        <span className="truncate">{column.label}</span>
                        {column.sortable && sortBy === column.key && (
                          <div className="flex items-center">
                            {sortOrder === 'asc' ? (
                              <ChevronUpIcon className="h-3 w-3 text-gray-700" />
                            ) : (
                              <ChevronDownIcon className="h-3 w-3 text-gray-700" />
                            )}
                          </div>
                        )}
                      </div>
                      <div
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-green-500 hover:w-1.5 transition-all"
                        onMouseDown={(e) => handleMouseDown(e, column.key)}
                        style={{ backgroundColor: isResizing && resizingColumn === column.key ? '#10b981' : 'transparent' }}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white">
                {loading ? (
                  <tr>
                    <td colSpan={columns.length + 2} className="px-6 py-12 text-center border-b border-gray-100">
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                        <span className="text-sm text-gray-500">Loading measurements...</span>
                      </div>
                    </td>
                  </tr>
                ) : measurements.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 2} className="px-6 py-12 text-center border-b border-gray-100">
                      <div className="text-sm text-gray-500">
                        No measurements found. Try adjusting your filters or search terms.
                      </div>
                    </td>
                  </tr>
                ) : (
                  measurements.map((measurement, rowIndex) => (
                    <tr
                      key={measurement.id}
                      className={`hover:bg-blue-50 transition-colors border-b border-gray-100 ${
                        selectedRows.find(row => row.id === measurement.id) ? 'bg-green-50' : rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      }`}
                    >
                      <td className="w-12 px-3 py-1 border-r border-gray-200 text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          checked={!!selectedRows.find(row => row.id === measurement.id)}
                          onChange={() => toggleRowSelection(measurement)}
                        />
                      </td>
                      <td className="w-24 px-3 py-1 text-center border-r border-gray-200">
                        <span className="isolate inline-flex rounded-md shadow-xs">
                          <button
                            type="button"
                            onClick={() => handleViewOnMap(measurement)}
                            title="View on Map"
                            disabled={!measurement.latitude || !measurement.longitude}
                            className={`relative inline-flex items-center justify-center rounded-l-md bg-white p-1.5 text-sm font-semibold ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus:z-10 ${
                              !measurement.latitude || !measurement.longitude 
                                ? 'text-gray-300 cursor-not-allowed' 
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                          >
                            <MapPinIcon className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleViewDetails(measurement)}
                            title="View Details"
                            className="relative -ml-px inline-flex items-center justify-center rounded-r-md bg-white p-1.5 text-sm font-semibold text-gray-400 ring-1 ring-gray-300 ring-inset hover:bg-gray-50 hover:text-gray-600 focus:z-10"
                          >
                            <EyeIconSolid className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      </td>
                      {columns.map((column) => (
                        <td 
                          key={column.key} 
                          className="px-3 py-1 text-xs text-gray-900 border-r border-gray-200"
                          style={{ 
                            width: `${columnWidths[column.key]}px`,
                            ...getCellStyle(measurement[column.key], column.key, measurement)
                          }}
                        >
                          <div className="truncate" title={column.format ? column.format(measurement[column.key], measurement) : (measurement[column.key] || '')}>
                            {column.format ? column.format(measurement[column.key], measurement) : (measurement[column.key] || '')}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer - Fixed */}
          <div className="flex-shrink-0 bg-gray-50 px-4 py-2 border-t border-gray-200 rounded-b-lg flex items-center justify-between text-sm">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              
              <div className="flex items-center space-x-2">
                <span className="text-gray-600">Page</span>
                <input
                  type="number"
                  min="1"
                  max={totalPages > 0 ? totalPages : 1}
                  value={currentPage}
                  onChange={(e) => {
                    const page = parseInt(e.target.value);
                    if (page >= 1 && page <= totalPages) {
                      setCurrentPage(page);
                    }
                  }}
                  className="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                />
                <span className="text-gray-600">of {totalPages > 0 ? totalPages : 1}</span>
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="flex items-center space-x-4">
              <select
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
                className="text-sm border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
              >
                <option value={25}>25 rows</option>
                <option value={50}>50 rows</option>
                <option value={100}>100 rows</option>
                <option value={250}>250 rows</option>
              </select>
              
              <span className="text-gray-600">{totalRecords.toLocaleString()} records</span>
            </div>
          </div>
        </div>
      </div>

      {/* Date Filter Dropdown */}
      <DateFilterDropdown
        isOpen={showDateFilter}
        onClose={() => setShowDateFilter(false)}
        buttonRef={dateFilterButtonRef}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
      />
      
      {/* Conditional Formatting Dropdown */}
      <ConditionalFormattingDropdown
        isOpen={showFormattingDropdown}
        onClose={() => setShowFormattingDropdown(false)}
        onAddRule={handleAddFormattingRule}
        buttonRef={formattingButtonRef}
        activeRules={conditionalFormattingRules}
        onRemoveRule={handleRemoveFormattingRule}
      />

      {/* Details Modal */}
      {showDetailsModal && selectedDetailsMeasurement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-gray-200">
            {/* Header */}
            <div className="bg-black px-4 py-3 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-bold">Measurement Details</h2>
                  <p className="text-blue-100 text-sm">
                    Project: {projectData?.name || 'Unknown Project'}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-full transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Operator</label>
                    <p className="text-gray-900">{selectedDetailsMeasurement.operator_name || 'Unknown'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Density</label>
                    <p className="text-gray-900">{selectedDetailsMeasurement.Density?.toFixed(1) || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Compaction</label>
                    <p className="text-gray-900">{selectedDetailsMeasurement.Compaction?.toFixed(1) || 'N/A'}%</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Surface Temperature</label>
                    <p className="text-gray-900">{selectedDetailsMeasurement.SurfaceTemp?.toFixed(1) || 'N/A'}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Local Time</label>
                    <p className="text-gray-900">{new Date(selectedDetailsMeasurement.LocalTimeOfMeasurement).toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Station</label>
                    <p className="text-gray-900">{selectedDetailsMeasurement.Station || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Description</label>
                    <p className="text-gray-900">{selectedDetailsMeasurement.Description1 || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Coordinates</label>
                    <p className="text-gray-900">{selectedDetailsMeasurement.latitude?.toFixed(5) || 'N/A'}, {selectedDetailsMeasurement.longitude?.toFixed(5) || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OnStation Modal */}
  {showOnStationModal && (
    <OnStationModal
      isOpen={showOnStationModal}
      onClose={() => setShowOnStationModal(false)}
      selectedMeasurements={selectedRows}
      onCloseWithReset={() => {
        setShowOnStationModal(false);
        setSelectedRows([]);
        setAllSelected(false);
        // Re-fetching integration logs will happen automatically via the useEffect
      }}
      onSubmit={(submittedMeasurements) => {
        console.log(`${submittedMeasurements.length} measurements prepared for OnStation.`);
      }}
    />
  )}
    </div>
  );
}